import React, { useEffect, useMemo, useState } from "react";
import { L as Link } from "../lib/i18n";
import { api } from "../lib/api";
import { fmtCHF } from "../lib/constants";
import {
  Eye, Heart, MessageSquare, GitCompareArrows, TrendingUp, Layers,
  ArrowUpDown, Sparkles, Loader2, ArrowUpRight, ArrowDownRight, Minus,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, Tooltip,
} from "recharts";

const COLUMNS = [
  { key: "title", label: "Inserat", sortable: true, align: "left" },
  { key: "views", label: "Views", sortable: true, align: "right" },
  { key: "trend", label: "Trend 30T", sortable: false, align: "left", hideMobile: true },
  { key: "favorites", label: "Favoriten", sortable: true, align: "right" },
  { key: "inquiries", label: "Anfragen", sortable: true, align: "right" },
  { key: "compare_views", label: "Vergleiche", sortable: true, align: "right" },
  { key: "days_online", label: "Tage online", sortable: true, align: "right" },
  { key: "ctr", label: "CTR", sortable: true, align: "right" },
];

function fmtNum(n) {
  return new Intl.NumberFormat("de-CH").format(n || 0);
}

/**
 * Trend pill — neutral grey when ~0, emerald positive, soft red negative.
 * "higherIsBetter=false" inverts sentiment (not used here, but reserved).
 */
function TrendPill({ pct, testId }) {
  const v = Number(pct) || 0;
  const abs = Math.abs(v);
  // Special signal: backend returns 999 when previous period was 0 but new data exists.
  // Render as "NEU" — friendlier than "+999%" for first-data-day case.
  const isFreshUp = v >= 999;
  // tolerance: ±3% counts as flat
  const flat = !isFreshUp && abs < 3;
  const positive = isFreshUp || v >= 3;
  const text = isFreshUp ? "NEU" : flat ? "0%" : `${v > 0 ? "+" : ""}${Math.round(v)}%`;
  const cls = flat
    ? "bg-zinc-800/70 text-zinc-400 border-zinc-700/60"
    : positive
      ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
      : "bg-red-500/10 text-red-300 border-red-500/30";
  const Icon = flat ? Minus : positive ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${cls}`}
      data-testid={testId}
    >
      <Icon className="h-3 w-3" />
      {text}
    </span>
  );
}

function KpiCard({ icon, label, value, hint, accent, trendPct, testId }) {
  return (
    <div
      className="rounded-lg border border-zinc-800 bg-zinc-950 p-4 sm:p-5"
      data-testid={testId}
    >
      <div className="flex items-center gap-2">
        <span className={`flex h-7 w-7 items-center justify-center rounded-md ${accent || "bg-red-600/15 text-red-500"}`}>
          {icon}
        </span>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">{label}</p>
      </div>
      <p className="mt-2 font-display text-2xl font-bold text-white sm:text-3xl" data-testid={`${testId}-value`}>{value}</p>
      <div className="mt-1 flex items-center gap-2 text-[11px] text-zinc-500">
        {hint && <span>{hint}</span>}
        {trendPct != null && <TrendPill pct={trendPct} testId={`${testId}-trend`} />}
      </div>
    </div>
  );
}

/**
 * Premium dual-line trend card — Views (red, prominent) + Inquiries (sky, slim).
 * Inquiries-Line wird nur gerendert, wenn überhaupt eine Anfrage in 30T existiert.
 */
function TrendCard({ trend }) {
  const days = trend?.days || [];
  const v = trend?.views_by_day || [];
  const i = trend?.inquiries_by_day || [];
  const series = days.map((d, idx) => ({ d, v: v[idx] || 0, i: i[idx] || 0 }));
  const has_inquiries = (trend?.inquiries_30d || 0) > 0;
  return (
    <div
      className="rounded-lg border border-zinc-800 bg-gradient-to-br from-zinc-950 via-zinc-950 to-zinc-900/40 p-4 sm:col-span-3 lg:col-span-6"
      data-testid="stats-trend-card"
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Performance · letzte 30 Tage</p>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-5 gap-y-1">
            <div className="flex items-baseline gap-2">
              <span className="inline-flex h-2 w-2 rounded-full bg-red-500" aria-hidden="true" />
              <span className="font-display text-2xl font-bold text-white sm:text-3xl">{fmtNum(trend?.views_30d || 0)}</span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-500">Views</span>
              <TrendPill pct={trend?.views_trend_pct} testId="trend-views-pct" />
            </div>
            {has_inquiries && (
              <div className="flex items-baseline gap-2">
                <span className="inline-flex h-2 w-2 rounded-full bg-sky-400" aria-hidden="true" />
                <span className="font-display text-xl font-semibold text-white sm:text-2xl">{fmtNum(trend?.inquiries_30d || 0)}</span>
                <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-500">Anfragen</span>
                <TrendPill pct={trend?.inquiries_trend_pct} testId="trend-inq-pct" />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="mt-3 h-24 w-full sm:h-28">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={series} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
            <defs>
              <linearGradient id="seller-trend-views" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#dc2626" stopOpacity={0.45} />
                <stop offset="100%" stopColor="#dc2626" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="seller-trend-inq" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#38bdf8" stopOpacity={0.18} />
                <stop offset="100%" stopColor="#38bdf8" stopOpacity={0} />
              </linearGradient>
            </defs>
            <Tooltip
              cursor={{ stroke: "#dc2626", strokeOpacity: 0.4, strokeWidth: 1 }}
              contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", borderRadius: 6, color: "#fff", fontSize: 12 }}
              labelFormatter={(label) => (label && label.length >= 10 ? `${label.slice(8, 10)}.${label.slice(5, 7)}.` : "")}
              formatter={(value, name) => {
                const label = name === "v" ? "Views" : "Anfragen";
                return [fmtNum(value), label];
              }}
            />
            <Area type="monotone" dataKey="v" stroke="#dc2626" strokeWidth={2} fill="url(#seller-trend-views)" isAnimationActive={false} />
            {has_inquiries && (
              <Area type="monotone" dataKey="i" stroke="#38bdf8" strokeWidth={1.5} fill="url(#seller-trend-inq)" isAnimationActive={false} />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Sparkline({ data, color = "#dc2626", testId }) {
  const series = (data || []).map((v, i) => ({ d: i, v: v || 0 }));
  const hasMovement = series.some((p) => p.v > 0);
  if (!hasMovement) return <span className="text-[10px] text-zinc-600" data-testid={testId}>—</span>;
  return (
    <div className="h-7 w-20" aria-hidden="true" data-testid={testId}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={series} margin={{ top: 2, right: 0, left: 0, bottom: 2 }}>
          <Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function KpiSkeleton() {
  return (
    <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="seller-stats-skeleton">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-[88px] animate-pulse rounded-lg border border-zinc-800 bg-zinc-950" />
      ))}
      <div className="h-[148px] animate-pulse rounded-lg border border-zinc-800 bg-zinc-950 sm:col-span-3 lg:col-span-6" />
    </div>
  );
}

export default function SellerStats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: "views", dir: "desc" });

  useEffect(() => {
    let alive = true;
    api.dashboardStats()
      .then((r) => { if (alive) setData(r); })
      .catch(() => { if (alive) setData({ totals: null, listings: [] }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const sortedListings = useMemo(() => {
    if (!data?.listings) return [];
    const arr = [...data.listings];
    const { key, dir } = sort;
    arr.sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "string" || typeof bv === "string") {
        return dir === "asc"
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      }
      return dir === "asc" ? av - bv : bv - av;
    });
    return arr;
  }, [data, sort]);

  const toggleSort = (key) => {
    setSort((p) => p.key === key
      ? { key, dir: p.dir === "asc" ? "desc" : "asc" }
      : { key, dir: key === "title" ? "asc" : "desc" });
  };

  if (loading) {
    return (
      <section className="mt-12" data-testid="seller-stats-loading">
        <div className="flex items-center gap-2">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-red-500">Verkäufer-Statistik</p>
        </div>
        <h2 className="mt-1 font-display text-xl font-semibold text-white sm:text-2xl">Performance-Übersicht</h2>
        <KpiSkeleton />
      </section>
    );
  }
  const t = data?.totals;
  if (!t || (t.total_listings || 0) === 0) return null;
  const trend = data?.trend || {};
  const has_30d = (trend?.views_30d || trend?.total_30d || 0) > 0;

  return (
    <section className="mt-12" data-testid="seller-stats-section">
      <div className="flex items-center gap-2">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-red-500">Verkäufer-Statistik</p>
      </div>
      <h2 className="mt-1 font-display text-xl font-semibold text-white sm:text-2xl">Performance-Übersicht</h2>

      {/* KPI Cards + Trend */}
      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="seller-stats-kpis">
        <KpiCard
          icon={<Eye className="h-3.5 w-3.5" />}
          label="Views"
          value={fmtNum(t.views)}
          trendPct={has_30d ? trend.views_trend_pct : null}
          testId="kpi-views"
        />
        <KpiCard icon={<Heart className="h-3.5 w-3.5" />} label="Favoriten" value={fmtNum(t.favorites)} testId="kpi-favs" />
        <KpiCard
          icon={<MessageSquare className="h-3.5 w-3.5" />}
          label="Anfragen"
          value={fmtNum(t.inquiries)}
          trendPct={(trend?.inquiries_30d || 0) > 0 ? trend.inquiries_trend_pct : null}
          testId="kpi-inq"
        />
        <KpiCard icon={<GitCompareArrows className="h-3.5 w-3.5" />} label="Vergleiche" value={fmtNum(t.compares)} testId="kpi-cmp" />
        <KpiCard
          icon={<Layers className="h-3.5 w-3.5" />}
          label="Aktiv"
          value={`${fmtNum(t.active_listings)}/${fmtNum(t.total_listings)}`}
          hint="Inserate"
          accent="bg-emerald-600/15 text-emerald-400"
          testId="kpi-active"
        />
        <KpiCard
          icon={<TrendingUp className="h-3.5 w-3.5" />}
          label="Conversion"
          value={`${t.conversion_pct}%`}
          hint="Anfragen ÷ Views"
          accent="bg-sky-600/15 text-sky-400"
          testId="kpi-conv"
        />
        {has_30d && <TrendCard trend={trend} />}
      </div>

      {/* Per-listing table */}
      <div className="mt-6 overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950">
        <div className="border-b border-zinc-800 px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">
          Pro Inserat
        </div>

        {/* Desktop / tablet: full table */}
        <div className="hidden overflow-x-auto sm:block">
          <table className="w-full text-sm" data-testid="seller-stats-table">
            <thead className="border-b border-zinc-800 bg-black/40">
              <tr>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    className={`whitespace-nowrap px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 ${c.align === "right" ? "text-right" : "text-left"} ${c.hideMobile ? "hidden md:table-cell" : ""}`}
                  >
                    {c.sortable ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(c.key)}
                        className={`inline-flex items-center gap-1 hover:text-white ${sort.key === c.key ? "text-white" : ""}`}
                        data-testid={`seller-stats-sort-${c.key}`}
                      >
                        {c.label}
                        <ArrowUpDown className={`h-3 w-3 ${sort.key === c.key ? "text-red-500" : "text-zinc-600"}`} />
                      </button>
                    ) : c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {sortedListings.map((it) => (
                <tr key={it.id} className="hover:bg-zinc-900/40" data-testid={`seller-stats-row-${it.id}`}>
                  <td className="max-w-[280px] px-3 py-3">
                    <Link to={`/listings/${it.id}`} className="flex items-center gap-2 group">
                      <div className="h-9 w-12 shrink-0 overflow-hidden rounded bg-zinc-900">
                        {it.image && (
                          <img
                            src={api.fileUrl(it.image)}
                            alt=""
                            className="h-full w-full object-cover"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="line-clamp-1 text-sm font-semibold text-white group-hover:text-red-300">{it.title}</p>
                        <p className="text-[10px] text-zinc-500">
                          {it.featured && <span className="mr-1 inline-flex items-center gap-0.5 text-red-400"><Sparkles className="h-2.5 w-2.5" />Boost</span>}
                          {it.is_active ? "Aktiv" : it.hidden ? "Versteckt" : (it.status || "—")}
                          {it.price_chf != null && <> · {fmtCHF(it.price_chf)}</>}
                        </p>
                      </div>
                    </Link>
                  </td>
                  <td className="px-3 py-3 text-right text-zinc-200" data-testid={`stats-cell-views-${it.id}`}>{fmtNum(it.views)}</td>
                  <td className="hidden px-3 py-3 md:table-cell" data-testid={`stats-cell-trend-${it.id}`}>
                    <div className="flex items-center gap-2">
                      <Sparkline data={it.sparkline_30d} testId={`stats-sparkline-${it.id}`} />
                      {(it.views_30d || 0) > 0 && (
                        <TrendPill pct={it.views_trend_pct} testId={`stats-trend-pct-${it.id}`} />
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right text-zinc-200">{fmtNum(it.favorites)}</td>
                  <td className="px-3 py-3 text-right text-zinc-200">{fmtNum(it.inquiries)}</td>
                  <td className="px-3 py-3 text-right text-zinc-200">{fmtNum(it.compare_views)}</td>
                  <td className="px-3 py-3 text-right text-zinc-400">{it.days_online != null ? `${it.days_online}d` : "—"}</td>
                  <td className="px-3 py-3 text-right">
                    <span className={`inline-flex rounded px-1.5 py-0.5 text-xs font-semibold ${it.ctr >= 5 ? "bg-emerald-500/15 text-emerald-300" : it.ctr >= 2 ? "bg-amber-500/15 text-amber-300" : "bg-zinc-800 text-zinc-400"}`}>
                      {it.ctr}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile: card list */}
        <ul className="divide-y divide-zinc-800 sm:hidden" data-testid="seller-stats-mobile-list">
          {sortedListings.map((it) => (
            <li key={it.id} className="p-4" data-testid={`seller-stats-mobile-row-${it.id}`}>
              <Link to={`/listings/${it.id}`} className="flex items-center gap-3">
                <div className="h-12 w-16 shrink-0 overflow-hidden rounded bg-zinc-900">
                  {it.image && (
                    <img
                      src={api.fileUrl(it.image)}
                      alt=""
                      className="h-full w-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="line-clamp-1 text-sm font-semibold text-white">{it.title}</p>
                  <p className="text-[11px] text-zinc-500">
                    {it.is_active ? "Aktiv" : it.hidden ? "Versteckt" : (it.status || "—")}
                    {it.days_online != null && <> · {it.days_online}d online</>}
                  </p>
                </div>
                <span className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] font-semibold ${it.ctr >= 5 ? "bg-emerald-500/15 text-emerald-300" : it.ctr >= 2 ? "bg-amber-500/15 text-amber-300" : "bg-zinc-800 text-zinc-400"}`}>
                  {it.ctr}%
                </span>
              </Link>
              {(it.views_30d || 0) > 0 && (
                <div className="mt-3 flex items-center justify-between rounded-md border border-zinc-800 bg-black/40 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Sparkline data={it.sparkline_30d} testId={`stats-sparkline-mobile-${it.id}`} />
                    <span className="text-[11px] text-zinc-400">{fmtNum(it.views_30d)} Views · 30T</span>
                  </div>
                  <TrendPill pct={it.views_trend_pct} testId={`stats-trend-pct-mobile-${it.id}`} />
                </div>
              )}
              <div className="mt-3 grid grid-cols-4 gap-2 text-center">
                <MobileMetric icon={<Eye className="h-3 w-3" />} label="Views" value={fmtNum(it.views)} />
                <MobileMetric icon={<Heart className="h-3 w-3" />} label="Favs" value={fmtNum(it.favorites)} />
                <MobileMetric icon={<MessageSquare className="h-3 w-3" />} label="Anfr." value={fmtNum(it.inquiries)} />
                <MobileMetric icon={<GitCompareArrows className="h-3 w-3" />} label="Vgl." value={fmtNum(it.compare_views)} />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function MobileMetric({ icon, label, value }) {
  return (
    <div className="rounded-md border border-zinc-800 bg-black/40 py-1.5">
      <div className="flex items-center justify-center gap-1 text-[10px] text-zinc-500">{icon} {label}</div>
      <p className="mt-0.5 text-sm font-semibold text-white">{value}</p>
    </div>
  );
}
